<script setup lang="ts">
import { login, ssoDiscover, ssoStartUrl } from "~/instances/KeurtApiClient";
import InputText from "primevue/inputtext";
import Button from "primevue/button"; // Ensure Button is imported

const route = useRoute();

const email = ref();
const password = ref();
const submitted = ref<boolean>(false);
const loginSucces = ref<boolean>(true);
const emailErrorMessage = ref<string>();
const passwordErrorMessage = ref<string>();
const loading = ref<boolean>(false); // New loading state

// SSO (Microsoft Entra ID)
const ssoAvailable = ref<boolean>(false);
const ssoEnforced = ref<boolean>(false);
const ssoError = ref<string | undefined>(
  typeof route.query.ssoError === "string" ? route.query.ssoError : undefined,
);
let lastDiscoveredEmail = "";
let discoverTimer: ReturnType<typeof setTimeout> | undefined;

const discoverSso = async () => {
  const value = String(email.value || "").trim();
  if (value === lastDiscoveredEmail) return;
  lastDiscoveredEmail = value;

  if (!validateEmail(value)) {
    ssoAvailable.value = false;
    ssoEnforced.value = false;
    return;
  }
  const result = await ssoDiscover(value);
  // Ignore stale responses when the user kept typing.
  if (value !== String(email.value || "").trim()) return;
  ssoAvailable.value = result.sso;
  ssoEnforced.value = result.enforced;
};

watch(email, () => {
  ssoAvailable.value = false;
  ssoEnforced.value = false;
  lastDiscoveredEmail = "";
  if (discoverTimer) clearTimeout(discoverTimer);
  discoverTimer = setTimeout(discoverSso, 400);
});

const loginWithMicrosoft = async () => {
  if (loading.value) return;
  submitted.value = true;
  if (!email.value || !validateEmail(email.value)) {
    emailErrorMessage.value = "E-mailadres is niet correct";
    return;
  }
  loading.value = true;
  try {
    window.location.href = await ssoStartUrl(String(email.value).trim());
  } catch {
    ssoError.value = "Inloggen via Microsoft kon niet worden gestart. Probeer het opnieuw.";
    loading.value = false;
  }
};

const tryLogin = async () => {
  submitted.value = true;
  ssoError.value = undefined;
  if (!email.value) {
    emailErrorMessage.value = "verplicht veld";
    return;
  }
  if (!validateEmail(email.value)) {
    emailErrorMessage.value = "E-mailadres is niet correct";
    return;
  }
  if (ssoEnforced.value) {
    loginWithMicrosoft();
    return;
  }
  if (!password.value) {
    passwordErrorMessage.value = "verplicht veld";
    return;
  }

  loading.value = true; // Start loading spinner
  try {
    const result = await login(email.value, password.value);
    loginSucces.value = result === "ok";
    if (result === "ok") {
      navigateAfterLogin();
    } else if (result === "sso") {
      ssoAvailable.value = true;
      ssoEnforced.value = true;
      passwordErrorMessage.value = "Dit account moet inloggen via Microsoft.";
    } else {
      passwordErrorMessage.value = "Wachtwoord incorrect";
    }
  } catch (error) {
    console.error("Login failed:", error);
  } finally {
    loading.value = false; // Stop loading spinner
  }
};

const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};
</script>

<template>
  <div class="grid min-h-screen place-content-center bg-app-background">
    <div class="flex flex-col justify-content w-80">
      <div class="theme-logo bg-center flex justify-center h-[70px] w-[160px] mx-auto mb-8" />
      <div
        v-if="ssoError"
        class="mb-6 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        {{ ssoError }}
      </div>
      <div class="flex flex-col">
        <label for="email" class="text-sm text-[#556D85]">E-mailadres</label>
        <InputText
          id="email"
          v-model="email"
          type="email"
          name="email"
          class="rounded-full"
          :class="{
            'p-invalid': submitted && (!email || !validateEmail(email)),
          }"
          placeholder="gebruiker@domein.nl"
          @blur="discoverSso()"
          @keyup.enter="tryLogin()"
        />
        <small
          v-if="submitted && (!email || !validateEmail(email))"
          class="p-error"
          >{{ emailErrorMessage }}</small
        >
      </div>

      <template v-if="!ssoEnforced">
        <div class="flex flex-col mt-9 mb-6">
          <label for="password" class="text-sm text-[#556D85]">Wachtwoord</label>
          <InputText
            id="password"
            v-model="password"
            type="password"
            name="password"
            :class="{ 'p-invalid': submitted && (!password || !loginSucces) }"
            class="rounded-full"
            @keyup.enter="tryLogin()"
          />
          <small
            v-if="submitted && (!password || !loginSucces)"
            class="p-error"
            >{{ passwordErrorMessage }}</small
          >
        </div>

        <Button
          class="w-80"
          label="Inloggen"
          rounded
          :loading="loading"
          @click="tryLogin()"
        />
      </template>

      <template v-if="ssoAvailable">
        <div v-if="!ssoEnforced" class="my-4 text-center text-sm text-[#556D85]">of</div>
        <div v-else class="mt-9 mb-2 text-sm text-[#556D85]">
          Je organisatie gebruikt Microsoft om in te loggen.
        </div>
        <Button
          class="w-80"
          label="Inloggen met Microsoft"
          icon="pi pi-microsoft"
          rounded
          :outlined="!ssoEnforced"
          :loading="loading && ssoEnforced"
          @click="loginWithMicrosoft()"
        />
      </template>
    </div>
    <a v-if="!ssoEnforced" class="flex justify-evenly mt-4" href="/requestPasswordReset"
      ><p class="text-[#FF6828]">Wachtwoord vergeten?</p></a
    >
  </div>
</template>
